import React, { useEffect, useState } from 'react';
import {
	Button,
	NavBar,
	Grid,
	Input,
	TextArea,
	Selector,
	Toast,
	Result,
} from 'antd-mobile';
import './styles.css';
import { options } from './option.js';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTonConnectUI, useTonWallet } from '@tonconnect/ui-react';
import { SmileOutline } from 'antd-mobile-icons';
import schools from '../../db/schools.json';
import {
	buildDonationTransaction,
	buildTonviewerUrl,
	getSignedBocHash,
} from './transaction';
import { useTonPrice, formatUsd, formatKrw } from '../../hooks/useTonPrice';
import { localizeSchool } from '../../db/localizeSchool';
import { useT } from '../../i18n';

const initialFlow = {
    status: 'editing',
    result: null,
    recording: null,
};

const getDonationPrefill = (searchParams) => {
    const presetAmount = searchParams.get('amount');
    const presetComment = searchParams.get('comment') || '';
    const matchedPreset = options.find((option) => option.value === presetAmount);
    return {
        value: matchedPreset ? matchedPreset.value : options[1].value,
        customAmount: presetAmount && !matchedPreset ? presetAmount : '',
        comment: presetComment,
    };
};

const getWalletProof = (wallet) => {
    const walletProof = wallet?.connectItems?.tonProof;
    const walletPublicKey = wallet?.account?.publicKey;
    return walletProof?.proof && walletPublicKey ? { walletProof, walletPublicKey } : null;
};

const buildDonationRecordBody = ({
    amountTon,
    comment,
    school,
    signedBocHash,
    submittedAt,
    wallet,
    walletProof,
    walletPublicKey,
}) => ({
    telegramInitData: (typeof window !== 'undefined' && window.Telegram?.WebApp?.initData) || '',
    schoolId: school.id,
    schoolName: school.name,
    recipientAddress: school.tonAddress,
    amountTon,
    comment,
    walletAddress: wallet?.account?.address,
    walletChain: wallet?.account?.chain,
    walletProof,
    walletPublicKey,
    signedBocHash,
    submittedAt,
});

const saveDonationRecord = (body) =>
    fetch('/api/donations', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
    });

const Donation = () => {
    const { schoolId } = useParams();
    const [searchParams] = useSearchParams();
    const { locale, t } = useT();
    const rawSchool = schools.find((item) => item.id === schoolId);
    const school = rawSchool ? localizeSchool(rawSchool, locale) : null;
    const prefill = getDonationPrefill(searchParams);
    const [value, setValue] = useState(prefill.value);
    const [customAmount, setCustomAmount] = useState(prefill.customAmount);
    const [comment, setComment] = useState(prefill.comment);
	const [flow, setFlow] = useState(initialFlow);

	const navigate = useNavigate();
	const [tonConnectUI] = useTonConnectUI();
	const wallet = useTonWallet();
	const loading = flow.status === 'submitting_wallet' || flow.status === 'submitted_recording';
	const showResult = flow.status !== 'editing' && flow.status !== 'submitting_wallet';
	const donationResult = flow.result;
	const recordingStatus = flow.recording;
	const { usd: tonUsd, krw: tonKrw } = useTonPrice();
	const activeAmount = customAmount.trim() || value;
    const usdLabel = formatUsd(activeAmount, tonUsd);
    const krwLabel = formatKrw(activeAmount, tonKrw);
    const hasFiatLabel = Boolean(usdLabel || krwLabel);

    useEffect(() => {
        setValue(prefill.value);
        setCustomAmount(prefill.customAmount);
        setComment(prefill.comment);
    }, [prefill.value, prefill.customAmount, prefill.comment]);

    const submit = async () => {
		if (!school) {
			Toast.show({ icon: 'fail', content: t('donation_target_not_found') });
			return;
		}
		if (!wallet) {
			navigate(`/connect?next=/donate/${school.id}`);
			return;
		}
        const proofFields = getWalletProof(wallet);
        if (!proofFields) {
            Toast.show({ icon: 'fail', content: t('wallet_verification_required') });
            navigate(`/connect?next=/donate/${school.id}`);
            return;
		}
		let transactionSubmitted = false;
		try {
			setFlow({ ...initialFlow, status: 'submitting_wallet' });
			const amountTon = customAmount.trim() || value;
			const tx = await buildDonationTransaction({
				address: school.tonAddress,
				amountTon,
				comment,
				from: wallet.account?.address,
			});
			const result = await tonConnectUI.sendTransaction(tx);
			const signedBocHash = getSignedBocHash(result?.boc);
			const submittedAt = new Date().toISOString();
			const nextDonationResult = {
				amountTon,
				comment,
				signedBocHash,
				explorerUrl: buildTonviewerUrl(signedBocHash),
				submittedAt,
			};
			setFlow({
				status: 'submitted_recording',
				result: nextDonationResult,
				recording: { status: 'pending', message: t('record_saving') },
			});
			transactionSubmitted = true;

            const recordResponse = await saveDonationRecord(buildDonationRecordBody({
                amountTon,
                comment,
                school,
                signedBocHash,
                submittedAt,
                wallet,
                ...proofFields,
            }));
			if (recordResponse.status === 202) {
				setFlow({
					status: 'record_failed',
					result: nextDonationResult,
					recording: {
						status: 'warning',
						message: t('history_disabled'),
					},
				});
			} else if (!recordResponse.ok) {
				setFlow({
					status: 'record_failed',
					result: nextDonationResult,
					recording: {
						status: 'warning',
						message: t('record_failed'),
					},
				});
			} else {
				setFlow({
					status: 'recorded',
					result: nextDonationResult,
					recording: { status: 'saved', message: t('record_saved') },
				});
			}
		} catch (err) {
			if (transactionSubmitted) {
				setFlow((currentFlow) => ({
					...currentFlow,
					status: 'record_failed',
					recording: {
						status: 'warning',
						message: t('record_failed'),
					},
				}));
				return;
			}
			Toast.show({
				icon: 'fail',
				content: err?.message || t('tx_rejected'),
			});
			setFlow(initialFlow);
		}
	};

	if (!school) {
		return (
			<div className="app">
				<div className="top">
					<NavBar onBack={() => navigate('/')}>{t('donation_target_not_found')}</NavBar>
				</div>
				<div className="content">
					<Button
						size="large"
						className="primary full_width"
						onClick={() => navigate('/')}
					>
						{t('back_button')}
					</Button>
				</div>
			</div>
		);
	}

	return (
		<div className="app">
			<div className="top">
				<NavBar onBack={() => navigate('/')}>{school.name}</NavBar>
			</div>
			{showResult && (
				<div className="content">
					<Result
						icon={<SmileOutline />}
						status="success"
						title={t('transaction_submitted')}
						description={t('transaction_submitted_desc')}
					/>
					<div className="donation_result">
						<p>
							<strong>{t('amount_summary')}</strong> {donationResult?.amountTon} TON
						</p>
						{recordingStatus && (
							<p className={`recording_status ${recordingStatus.status}`}>
								{recordingStatus.message}
							</p>
						)}
						{donationResult?.signedBocHash && (
							<p>
								<strong>{t('message_hash')}</strong>{' '}
								<span className="hash_text">{donationResult.signedBocHash}</span>
							</p>
						)}
						{donationResult?.explorerUrl && (
							<Button
								size="large"
								className="full_width"
								onClick={() =>
									window.open(
										donationResult.explorerUrl,
										'_blank',
										'noopener,noreferrer'
									)
								}
							>
								{t('open_tonviewer')}
							</Button>
						)}
					</div>
					<Button
						size="large"
						className="primary full_width"
						onClick={() => navigate('/')}
					>
						{t('back_button')}
					</Button>
				</div>
			)}
			{!showResult && (
				<>
					<div className="content">
						<Grid columns={3} gap={2}>
							<Grid.Item span={3}>
								<p>{t('amount_label')}</p>
							</Grid.Item>
							<br />
							<Grid.Item span={3}>
								<Selector
									style={{
										'--border-radius': '10px',
										'--border': 'solid transparent 1px',
										'--checked-border':
											'solid var(--adm-color-primary) 1px',
										'--padding': '8px 24px',
									}}
									showCheckMark={false}
									options={options}
									value={[value]}
									disabled={loading}
									onChange={(e) => {
										setValue(e[0]);
										setCustomAmount('');
									}}
								/>
							</Grid.Item>
							<Grid.Item span={3}>
								<p>{t('custom_amount_label')}</p>
								<Input
									className="custom_amount"
									placeholder={t('custom_amount_placeholder')}
									type="number"
									inputMode="decimal"
									value={customAmount}
									disabled={loading}
									onChange={setCustomAmount}
								/>
								{hasFiatLabel && (
									<p className="usd_label">
										{activeAmount} TON
										{usdLabel && ` · ${usdLabel}`}
										{krwLabel && ` · ${krwLabel}`}
									</p>
								)}
							</Grid.Item>
							<p>{t('comment_label')}</p>
							<Grid.Item span={3}>
								<TextArea
									placeholder={t('comment_placeholder')}
									value={comment}
									disabled={loading}
									autoSize={{ minRows: 5, maxRows: 7 }}
									style={{ backgroundColor: 'white', padding: '5px' }}
									onChange={(val) => setComment(val)}
								/>
							</Grid.Item>
						</Grid>
					</div>
					<div className="donate__button">
						<Button
							size="large"
							className="primary button"
							onClick={submit}
							loading={loading}
							loadingText={t('loading')}
						>
							{wallet ? t('donate_button') : t('connect_wallet_button')}
						</Button>
					</div>
				</>
			)}
		</div>
	);
};

export default Donation;
